import { P2GBox, P2GText } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { Fragment, ReactNode, useState } from 'react';
import {
  ConsignmentApiModel,
  Country,
  Dimensions,
  InputRuleApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';

import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { Splitting } from '../grouping/Splitting';
import { Combining } from '../grouping/Combining';
import { SingleItemOptions } from '../grouping/SingleItemOptions';
import { ShowSingleItemOptions } from '~/apps/smartsend/lib/parcels/singleItemOptions';
import { Custom } from '../grouping/Custom';
import { useContext } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  AddNewItemsAction,
  SaveItemsAction,
  SaveTagsAction,
  SmartSendItemsAddedSocketAction,
  SmartSendItemsUpdatedSocketAction
} from '../../../lib/context/SmartSendContextModels';
import { SocketItemUpdateModel } from '../SmartSendSocketsModels';
import { useMemo } from 'react';
import { useEffect } from 'react';
import { Convert } from '../grouping/Convert';
import { IsReturnCollection } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { uid } from '../../../lib/helpers/uid';
import { EventSections } from '../../../lib/events/EventLogger';
import { FilterToSendableItems } from './FilterToSendableItems';
import { GenerateGroupSummary } from './GenerateGroupSummary';
import { GroupingFlow, ResolveGroupingFlow } from './ResolveGroupingFlow';
import { resolveConsignmentTarget } from '~/apps/smartsend/lib/parcels/commit';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { Tagged } from '../../Tagged';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useCreateItemsHelper from '../../../lib/helpers/useItemCreateHelper';
import { P2GSidePanel } from '~/components/sidepanel';
import { P2GImageMessage } from '~/components/imagemessage';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { ContainsMarketplaceWithInternalSplitting } from '~/apps/smartsend/lib/helpers/ContainsMarketplaceWithInternalSplitting';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

export interface GroupingPanelProps {
  source: 'ITEM-GROUP' | 'ITEM-PANEL';
  group: SmartSendItemGroup;
  onComplete?: () => void;
  control?: ReactNode;
  countries?: Country[];
  productValidation?: InputRuleApiModel[];
  tariffSystem?: string;
}

export function ShowGroup(items: SmartSendItemApiModel[]): boolean {
  if (!SettingsCache.settings.Features.includes('ParcelSplitting')) {
    return false;
  }

  if (ContainsMarketplaceWithInternalSplitting(items)) {
    return false;
  }

  return (
    !items.find(IsReturnCollection) &&
    items.length > 0 &&
    items.every((i) => i.MarketplaceStatus === 'Sold') &&
    items.every((i) => i.Status === 'Active') &&
    items.length == items.filter((x) => x.PropertyKey === items[0].PropertyKey).length
  );
}

type groupingFlow = GroupingFlow;

export const GroupingPanel: React.FC<GroupingPanelProps> = (props) => {
  const { createItemsHelper } = useCreateItemsHelper();
  const { createGroup, getPackages } = useSmartSendApi();
  const ctx = useContext(SmartSendContext);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<'PROCESSING' | 'IDLE' | 'ERROR'>('IDLE');
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const key = useMemo(() => {
    let k = '';
    props.group.Items.forEach((i) => {
      k += `${i.Id}_`;
      i.MarketplaceOrderLines.filter(FilterToSendableItems).forEach((o) => {
        k += `${o.Id}_${o.Quantity}_`;
      });
    });
    return k;
  }, [props.group, props.group.Items.length]);

  const groupSummary = useMemo(() => GenerateGroupSummary(props.group.Items), [key, props.group]);

  const allowSplitToDifferentServices = groupSummary.smartSendItemCount > 1 && groupSummary.totalOrders > 2;

  const singleItemOptions = useMemo(
    () => ShowSingleItemOptions(props.source, props.group.Items),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, props.group, props.source]
  );

  // Exactly one flow applies to any given group - a pure derivation from the summary, so it is a
  // memo rather than state kept in sync by an effect.
  const flow = useMemo<groupingFlow>(
    () => ResolveGroupingFlow(groupSummary, SettingsCache.settings.Features.includes('ParcelSplitting')),
    [groupSummary]
  );

  useEffect(() => {
    if (open && flow) {
      track({
        type: 'smartsend_grouping_tab_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem, 'grouping']),
          tab: flow.toLowerCase()
        }
      });
    }
  }, [open]);

  // The heading describes what the screen actually offers, so it is "Shipment Options" wherever the
  // customer is being given a choice of outcomes - the group case, and now a single item from the list.
  // It stays descriptive where there is only the one split flow and no choice to make.
  const title = useMemo(() => {
    if (singleItemOptions) {
      return t('GROUP_SHIPMENT_OPTIONS_TITLE', { defaultValue: 'Shipment Options' });
    }

    switch (flow) {
      case 'GROUP':
        return t('GROUP_SHIPMENT_OPTIONS_TITLE', { defaultValue: 'Shipment Options' });
      case 'SPLIT':
      case 'CONVERT':
      case 'CUSTOM':
        return t('SPLIT_AND_SCHEDULE_SEPARATELY_TITLE', {
          defaultValue: 'Split and Ship Separately'
        });
      default:
        return t('GROUP_PARCELS_TITLE', { defaultValue: 'Group Parcels' });
    }
  }, [flow, singleItemOptions, t]);

  async function createItems(items: SmartSendItemApiModel[]) {
    setState('PROCESSING');
    const resp = await createGroup(items);

    if (resp.success) {
      const actions: SocketItemUpdateModel[] = [];

      // Deletes and Updates
      resp.result.DeletedItems.forEach((id) => {
        actions.push({ Id: id, ItemUpdates: { Status: 'Deleted' } });
      });
      resp.result.ModifiedItems.forEach((i) => {
        actions.push({ Id: i.Id, ItemUpdates: { MarketplaceOrderLines: i.MarketplaceOrderLines, Value: i.Value } });
      });
      ctx.dispatch(new SmartSendItemsUpdatedSocketAction({ Items: actions }));
      // New Items
      ctx.dispatch(new SmartSendItemsAddedSocketAction(resp.result.AddedItems, 0));

      // createGroup's request payload has no field for Consignment, so a merged item created with a
      // multi-parcel plan needs a follow-up save once it has a real Id.
      const target = resolveConsignmentTarget(items, resp.result.AddedItems);

      if (target === 'AMBIGUOUS') {
        // No match, or more than one candidate - attaching the consignment to the wrong shipment
        // would mis-declare it, so surface an error rather than guess.
        setState('ERROR');
        return;
      }

      if (target !== 'NONE') {
        ctx.dispatch(new SaveItemsAction([target.itemId], ['Consignment'], { Consignment: target.consignment }));
      }

      setState('IDLE');
      setOpen(false);

      props.onComplete && props.onComplete();
    } else {
      setState('ERROR');
    }
  }

  async function createSplitItems(item: SmartSendItemApiModel, newItems: SmartSendItemApiModel[], save: string[]) {
    setState('PROCESSING');
    let success = true;

    if (save.includes('Tags')) {
      ctx.dispatch(new SaveTagsAction([item.Id], item.Tags));
    }

    if (save.includes('MarketplaceOrderLines')) {
      ctx.dispatch(
        new SaveItemsAction([item.Id], ['MarketplaceOrderLines'], {
          MarketplaceOrderLines: item.MarketplaceOrderLines
        })
      );
    }

    await createItemsHelper(
      newItems.map((x) => {
        return { id: uid(), item: x, raw: [] };
      }),
      (items) => {
        ctx.dispatch(new AddNewItemsAction(items));
      },
      () => {
        success = false;
      },
      () => {}
    );

    if (success) {
      setState('IDLE');
      setOpen(false);

      props.onComplete && props.onComplete();
    } else {
      setState('ERROR');
    }
  }

  // Patches the consignment onto the item that is already there - no createGroup, no replacement item.
  // The observer requotes off the back of this because the item comes out of SAVING with unsaved fields.
  function saveConsignment(itemId: string, consignment: ConsignmentApiModel, dimensions: Dimensions) {
    ctx.dispatch(
      new SaveItemsAction([itemId], ['Consignment', 'Dimensions'], { Consignment: consignment, Dimensions: dimensions })
    );
    setOpen(false);
    props.onComplete && props.onComplete();
  }

  if (!props.group || (!flow && !singleItemOptions)) {
    return null;
  }

  return (
    <Fragment key={key}>
      {props.control ? (
        <P2GBox
          onClick={() => {
            setOpen(true);
          }}
        >
          {props.control}
        </P2GBox>
      ) : (
        <P2GLink
          onClick={() => {
            setOpen(true);
          }}
        >
          {title}
        </P2GLink>
      )}
      <P2GSidePanel
        anchor="right"
        open={open}
        maxWidth="md"
        onClose={() => setOpen(false)}
        title={t('GROUPING_PANEL_TITLE', { defaultValue: 'Shipment Options' })}
        width="600px"
      >
        {open && (
          <Tagged tag="panel-grouping">
            <P2GBox textAlign="left">
              <P2GText variant="subtitle2">{title}</P2GText>
              {state === 'IDLE' && (
                <P2GBox>
                  <P2GBox pb={2} pt={2}>
                    {singleItemOptions ? (
                      <SingleItemOptions
                        group={props.group}
                        splitFlow={flow === 'GROUP' ? '' : flow}
                        countries={props.countries}
                        productValidation={props.productValidation}
                        tariffSystem={props.tariffSystem}
                        getPackages={async () => {
                          const result = await getPackages();
                          return result?.result?.Packages ?? [];
                        }}
                        saveConsignment={saveConsignment}
                        createItems={createItems}
                        createSplitItems={createSplitItems}
                        cancel={() => setOpen(false)}
                      />
                    ) : (
                      <>
                        {flow === 'GROUP' && (
                          <Combining
                            group={props.group}
                            create={createItems}
                            getPackages={async () => {
                              const result = await getPackages();
                              return result?.result?.Packages ?? [];
                            }}
                            countries={props.countries}
                            productValidation={props.productValidation}
                            tariffSystem={props.tariffSystem}
                            allowSplitToDifferentServices={allowSplitToDifferentServices}
                          />
                        )}
                        {flow === 'SPLIT' && (
                          <Splitting
                            group={props.group}
                            split={(i, n) => createSplitItems(i, n, ['Tags', 'MarketplaceOrderLines'])}
                            getPackages={async () => {
                              const result = await getPackages();
                              return result?.result?.Packages ?? [];
                            }}
                            countries={props.countries}
                            productValidation={props.productValidation}
                            tariffSystem={props.tariffSystem}
                            cancel={() => {
                              setOpen(false);
                            }}
                          />
                        )}
                        {flow === 'CONVERT' && (
                          <Convert
                            group={props.group}
                            split={(i, n) => createSplitItems(i, n, ['MarketplaceOrderLines'])}
                            countries={props.countries}
                            productValidation={props.productValidation}
                            tariffSystem={props.tariffSystem}
                            cancel={() => {
                              setOpen(false);
                            }}
                          />
                        )}
                        {flow === 'CUSTOM' && (
                          <Custom
                            group={props.group}
                            create={createItems}
                            countries={props.countries}
                            productValidation={props.productValidation}
                            tariffSystem={props.tariffSystem}
                            getPackages={() => getPackages().then((r) => (r.success ? r.result?.Packages || [] : []))}
                          />
                        )}
                      </>
                    )}
                  </P2GBox>
                </P2GBox>
              )}
              {state === 'PROCESSING' && (
                <P2GImageMessage
                  image="/theme/smartsend/notifications/item-loading.png"
                  message={t('PROCESSING_FALLBACK', { defaultValue: 'Processing...' })}
                  contentKey="image-smartsend-grouping-loading"
                />
              )}
              {state === 'ERROR' && (
                <P2GImageMessage
                  image="/theme/smartsend/notifications/item-error.png"
                  message={t('THERE_WAS_ISSUE_ERROR_FALLBACK', {
                    defaultValue: 'There was an issue creating your grouped item...'
                  })}
                  contentKey="image-smartsend-grouping-error"
                />
              )}
            </P2GBox>
          </Tagged>
        )}
      </P2GSidePanel>
    </Fragment>
  );
};
